interface MilesThinkingIndicatorProps {
  /**
   * The message to display while thinking.
   * @default "Analyzing your data..."
   */
  message?: string;
  /**
   * Size variant: 'inline' for message bubbles, 'centered' for full-screen loading
   */
  variant?: 'inline' | 'centered';
}

/**
 * MilesThinkingIndicator - A premium loading state for MILES.
 *
 * Replaces generic spinners with a branded animation that reinforces
 * MILES' identity as a financial assistant. The truck animation with
 * dollar signs creates a sense that "your data is being processed."
 *
 * Design principles (Robinhood-inspired):
 * - Loading should feel purposeful, not just waiting
 * - Animation reinforces what's happening (financial analysis)
 * - Professional messaging ("Analyzing" not "Loading")
 */
function MilesThinkingIndicator({
  message = 'Analyzing your data...',
  variant = 'inline',
}: MilesThinkingIndicatorProps) {
  if (variant === 'centered') {
    return (
      <div className="flex h-full flex-col items-center justify-center py-8">
        {/* Thinking message with animated dots */}
        <div className="mt-4 flex items-center gap-1.5">
          <span className="text-sm font-medium text-gray-600">{message}</span>
          <ThinkingDots />
        </div>

        {/* Subtle progress hint */}
        <p className="mt-2 text-xs text-gray-400">
          This usually takes a few seconds
        </p>
      </div>
    );
  }

  // Inline variant for message bubbles
  return (
    <div className="flex items-center gap-3 py-1">
      {/* <div className="relative">
        <Lottie
          animationData={truckAnimation}
          loop={true}
          autoplay={true}
          style={{ width: 32, height: 32 }}
        />
      </div> */}
      <div className="flex items-center gap-1">
        <span className="text-sm text-gray-500">{message}</span>
        <ThinkingDots />
      </div>
    </div>
  );
}

/**
 * Animated dots that pulse sequentially - more elegant than "..."
 */
function ThinkingDots() {
  return (
    <span className="flex gap-0.5">
      <span
        className="h-1 w-1 animate-pulse rounded-full bg-gray-400"
        style={{ animationDelay: '0ms' }}
      />
      <span
        className="h-1 w-1 animate-pulse rounded-full bg-gray-400"
        style={{ animationDelay: '150ms' }}
      />
      <span
        className="h-1 w-1 animate-pulse rounded-full bg-gray-400"
        style={{ animationDelay: '300ms' }}
      />
    </span>
  );
}

export default MilesThinkingIndicator;
